import React, { useState, useRef } from "react";
import "./Hero.css";
import { useBrand } from "../../context/brand-context";
import { MdDirectionsCar } from "react-icons/md";
import { FiSearch, FiTruck, FiTag, FiCheckCircle } from "react-icons/fi";
import http from "../../api/http";
import { useAuth } from "../../context/AuthContext";
import { detectInputType } from "../../utils/vin-validation";
import carBg from "../../images/bg-car.png";

export default function Hero({ onVinResult }) {
  const { brand } = useBrand();
  const { user } = useAuth();

  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Parallaxe douce : la souris pilote --mx/--my (décalage des blobs)
  const heroRef = useRef(null);
  const onMouseMove = (e) => {
    const el = heroRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", ((e.clientX - r.left) / r.width - 0.5).toFixed(3));
    el.style.setProperty("--my", ((e.clientY - r.top) / r.height - 0.5).toFixed(3));
  };
  const onMouseLeave = () => {
    const el = heroRef.current;
    if (!el) return;
    el.style.setProperty("--mx", "0");
    el.style.setProperty("--my", "0");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = vehicleId.trim();
    const inputType = detectInputType(id);

    if (inputType === "empty")   { setError("Veuillez saisir un VIN (17 car.) ou un identifiant RepXpert."); return; }
    if (inputType === "invalid") { setError("Format invalide : VIN 17 caractères (ex: VF7CCHNZ3AW010101) ou ID numérique (ex: 55099)."); return; }

    setError("");
    setLoading(true);
    try {
      const codtiers = user?.codtiers || null;
      const body = {
        ...(inputType === "vin" ? { vin: id.toUpperCase() } : { externalId: id }),
        ...(codtiers ? { codtiers } : {}),
      };
      // by-vehicle cumule scraping RepXpert + SQL (20-60s) : timeout dédié
      const { data } = await http.post("/api/articles/by-vehicle", body, { timeout: 120000 });
      onVinResult?.(data);
    } catch (err) {
      const msg = err?.code === "ECONNABORTED"
        ? "La recherche a pris trop de temps. Réessayez."
        : "Erreur lors de la recherche. Vérifiez votre connexion.";
      setError(err?.response?.data?.error || msg);
      onVinResult?.(null);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setVehicleId(e.target.value);
    if (error) setError("");
  };

  return (
    <section
      ref={heroRef}
      className={`hero hero--${brand}`}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    >
      {/* Fond : photo (même que la page d'accueil publique) + voile */}
      <div className="hero__bg" style={{ backgroundImage: `url(${carBg})` }} aria-hidden="true" />
      <div className="hero__overlay" aria-hidden="true" />

      {/* Fond : aurora animée + grille subtile */}
      <div className="hero__aurora" aria-hidden="true">
        <span className="hero__blob hero__blob--1" />
        <span className="hero__blob hero__blob--2" />
        <span className="hero__blob hero__blob--3" />
      </div>
      <div className="hero__grid" aria-hidden="true" />

      <div className="hero__inner">
        <span className="hero__eyebrow">
          <span className="hero__eyebrow-dot" /> Catalogue B2B · pièces auto · Tunisie
        </span>

        <h1 className="hero__h1">
          Trouvez <span className="hero__accent">la bonne pièce</span>,<br />
          en quelques secondes.
        </h1>

        <p className="hero__lead">
          Recherchez par VIN ou identifiant véhicule dans le plus grand catalogue
          de pièces d'origine et adaptables de Tunisie.
        </p>

        <form className={`hero__search glow-border${error ? " hero__search--error" : ""}`} onSubmit={handleSubmit} noValidate role="search">
          <span className="hero__search-ic" aria-hidden="true"><MdDirectionsCar size={22} /></span>
          <input
            className="hero__search-input"
            value={vehicleId}
            onChange={handleChange}
            placeholder="Entrez un VIN (17 car.) ou un ID véhicule…"
            aria-label="VIN ou identifiant véhicule RepXpert"
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
          />
          <button type="submit" className="hero__go" disabled={loading}>
            {loading ? <span className="hero__spinner" /> : <><FiSearch /> <span className="hero__go-txt">Rechercher</span></>}
          </button>
        </form>

        {error && <p className="hero__error">{error}</p>}
        <p className="hero__hint">VIN 17 caractères ou identifiant numérique RepXpert</p>

        <div className="hero__chips">
          <span className="hero__chip"><FiCheckCircle /> 50 000+ références</span>
          <span className="hero__chip"><FiTruck /> Livraison 24h</span>
          <span className="hero__chip"><FiTag /> Origine &amp; adaptable</span>
        </div>
      </div>
    </section>
  );
}
